import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { fingerScan } from "../utils/careplix-web-algo";
import { useAuth } from "../utils/auth";
import { sdkURL, api_key } from "../utils/api-url-list";
import { getCurrentLocation } from "../utils/geoLocation";

import LoadingScreen from "../components/LoadingScreen";

import Failure from "../assets/images/failure.gif";
import HertBeat from "../assets/images/heartbeat.gif";
import Analyzing from "../assets/images/analyzing.gif";

const scanMessages = [
  "We burn calories while we are asleep because brain activity requires energy",
  "Exercise promotes cardiovascular health, ensuring more blood and oxygen to circulate the body, helping to elevate energy",
  "Always sleep on your back straight since it allows your neck and spine in a neutral position",
  "Alcohol consumption disrupts chemical impulses between brain cells causing impulsive behaviour, slurred speech, impaired memory",
  "Meditation increases nitric oxide generation in the brain helping to lower your blood pressure",
  "Meditation reduces the production of cytokines in several recent studies, thus helping in stress reduction",
  "Laughing is good for your heart. It reduces stress and gives a boost to your immune system",
  "Lack of sleep can lead to heart disease, heart attack, or stroke",
  "Exercise boosts brain performance",
  "Heart pumps about 2,000 gallons of blood every day",
  "Your heart will beat about 115,000 times each day",
].sort(() => Math.random() - 0.5);

const getMessage = (type = "", timeElapsed = 0) => {
  if (type !== "scan") {
    if (timeElapsed <= 2500) return "Cover your back camera with your finger";
    else if (timeElapsed <= 5000)
      return "During the measurement, please do not speak or move";
    else if (timeElapsed <= 7000)
      return "Keep your device steady throughout the vital measurement";
    else if (timeElapsed <= 8000) return "We are good to start in 3";
    else if (timeElapsed <= 9000) return "We are good to start in 2";
    else if (timeElapsed <= 10000) return "We are good to start in 1";
  } else {
    if (timeElapsed <= 15000) return "Scan in Progress...";
    else if (timeElapsed <= 20000) return scanMessages[0];
    else if (timeElapsed <= 25000) return scanMessages[1];
    else if (timeElapsed <= 30000) return scanMessages[2];
    else if (timeElapsed <= 35000) return scanMessages[3];
    else if (timeElapsed <= 40000) return scanMessages[4];
    else if (timeElapsed <= 45000) return scanMessages[5];
    else if (timeElapsed <= 50000) return scanMessages[6];
    else if (timeElapsed <= 55000) return scanMessages[7];
    else if (timeElapsed <= 60000) return scanMessages[8];
    else if (timeElapsed <= 65000) return scanMessages[9];
    else if (timeElapsed <= 70000) return scanMessages[10];
  }
};

const FingerScan = () => {
  const videoInputRef = useRef();
  const canvasOutputRef = useRef();

  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [scanFrameData, setScanFrameData] = useState({
    type: "",
    timeElapsed: 0,
    percentage: 0,
    isFingerInView: false,
  });

  const { userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const controller = new AbortController();
    const landscape = window.matchMedia("(orientation: landscape)");
    if (location.state?.scanToken?.length > 0) {
      fingerScan.onFrame(setScanFrameData);
      fingerScan.onScanFinish(
        async ({ raw_intensity, ppg_time, average_fps }) => {
          try {
            controller.abort();
            setAnalyzing(true);
            const coords = await getCurrentLocation();
            const resp = await fetch(sdkURL("/vitals/add-scan"), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                api_key,
                scan_token: location.state.scanToken,
                employee_id: userData.employee_id,
                posture: location.state?.posture ?? "resting",
                dob: userData.dob,
                gender: userData.gender,
                metadata: {
                  physiological_scores: {
                    height: userData.height,
                    weight: userData.weight,
                  },
                  ppg_time: ppg_time,
                  raw_intensity: raw_intensity,
                  device: navigator.platform.match(/iPhone|iPod|iPad/)
                    ? "RPPG_CAREPLIX_FINGER_IOS"
                    : "RPPG_CAREPLIX_FINGER_ANDROID",
                  fps: average_fps,
                },
                lat: coords.lat,
                long: coords.long,
                notes: {
                  referral_code: localStorage.getItem("referral") || "",
                  geo_location: coords,
                },
              }),
            });
            const resp_json = await resp.json();
            if (resp_json.statusCode?.toString().startsWith("2"))
              navigate("/scan-result", {
                state: { result: resp_json, popup_time: Date.now() + 5000 },
                replace: true,
              });
            else throw new Error(resp_json.message);
          } catch (err) {
            console.error(err);
            setError(err.message);
          }
        }
      );
      fingerScan.onError((err) => {
        console.error(err);
        setError(err.message);
      });
      fingerScan
        .startScan({
          videoElement: videoInputRef.current,
          canvasElement: canvasOutputRef.current,
        })
        .then(() => {
          console.log("Scan Started");
          landscape.addEventListener(
            "change",
            (e) => {
              if (e.matches) {
                fingerScan.stopScan(true);
                const err = new Error(
                  "Screen orientation changed to Landscape"
                );
                console.error(err);
                setError(err.message);
              }
            },
            { signal: controller.signal }
          );
        })
        .catch((err) => {
          console.log("Scan Failed");
          console.error(err);
        });
    } else setError("Invalid Scan Token");
    return () => {
      fingerScan.stopScan(true);
      controller.abort();
    };
  }, [location, navigate, userData]);

  return (
    <div className="relative h-screen bg-white">
      {error.length > 0 ? (
        <div className="h-full px-8 py-12 flex flex-col items-start justify-center">
          <img className="shrink-0 w-20" src={Failure} alt="failure" />
          <p className="mt-4 text-error text-2xl font-semibold">Scan Failed!</p>
          <p className="mt-2 max-h-40 overflow-auto whitespace-pre-line font-medium">
            {error}
          </p>
          <button
            className="self-end mt-8 px-6 py-2 rounded-full bg-primary text-white font-medium"
            type="button"
            onClick={() => {
              navigate("/scan-instructions", {
                state: { scanType: "finger" },
                replace: true,
              });
            }}
          >
            Try Again&nbsp;&nbsp;&rarr;
          </button>
        </div>
      ) : analyzing ? (
        <div className="h-full px-8 py-12 flex flex-col items-start justify-center">
          <img className="shrink-0 w-20" src={Analyzing} alt="analyzing" />
          <p className="mt-4 text-primary text-2xl font-semibold">
            Analyzing Data
          </p>
          <p className="mt-2 font-medium">
            Continuing analysis for
            <br />
            accurate measurements...
          </p>
        </div>
      ) : (
        <>
          <video
            className="fixed bottom-12 left-8 w-px h-px"
            id="videoInput"
            ref={videoInputRef}
            autoPlay
            muted
            playsInline
          />
          <canvas
            id="canvasOutput"
            ref={canvasOutputRef}
            className="h-full w-full -scale-x-100"
          />
          <div className="fixed left-0 right-0 top-[30vh]">
            {scanFrameData.isFingerInView ? (
              <img src={HertBeat} alt="heartbeat" className="w-48 mx-auto" />
            ) : (
              <>
                <svg className="w-48 mx-auto" viewBox="0 0 300 90">
                  <circle fill="#fff" cx="278.63" cy="55.48" r="13.33" />
                  <polyline
                    fill="none"
                    stroke="#fff"
                    strokeWidth={5}
                    strokeMiterlimit={10}
                    points="7.75,54.04 74.69,54.04 83.58,21.09 96.13,72.86 102.93,54.04 118.61,53.51 130.12,10.11 158.36,80.71 169.86,54.04 189.73,54.04 198.1,23.18 200.71,23.18 212.22,68.68 217.45,54.04 265.57,53.96"
                  />
                </svg>
                <p className="mt-2 text-xl text-center text-white drop-shadow">
                  Cannot Detect Finger
                </p>
              </>
            )}
          </div>
          <div className="fixed bottom-10 left-6 right-6 p-6 rounded-xl bg-white/80 flex flex-col">
            <h3 className="text-primary font-semibold">
              {scanFrameData.type === "scan"
                ? `${scanFrameData.percentage}% Completed...`
                : `Calibration in progress...`}
            </h3>
            <h4 className="mt-2 text-primary text-xs">
              {getMessage(scanFrameData.type, scanFrameData.timeElapsed)}
            </h4>
            <button
              type="button"
              onClick={() => {
                fingerScan.stopScan(true);
                navigate("/scan-instructions", {
                  state: { scanType: "finger" },
                  replace: true,
                });
              }}
              className="self-end mt-4 text-error text-sm font-medium"
            >
              &lt;&nbsp;Cancel Scan
            </button>
          </div>
          {fingerScan.isInitializing() && (
            <div className="absolute top-0 bottom-0 left-0 right-0 bg-white px-8 py-12 flex flex-col items-center justify-center text-center">
              <LoadingScreen />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FingerScan;
